// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { ActionOutput, ReportOutput } from 'thehive-sdk';
import { classList, useLocalMessage } from '@Helpers/';
import { ColumnsRendererType } from '@Hooks/useConfigList/helper/computeHeader';
import THTag from '@Atoms/THTag/THTag';
import { HTMLColors } from '@Constants/colors';
import { THList } from '@Molecules/THList';
import { ExpandableConfig } from 'antd/lib/table/interface';
import { ColumnTitle } from '@Atoms/ColumnTitle/ColumnTitle';
import { FormatDate } from '@Molecules/Format';

import './ResponderReports.scss';

interface ResponderReportsProps {
    reports?: ActionOutput[];
    columnsRenderer: ColumnsRendererType<ActionOutput>;
}

const ResponderReports = ({ reports, columnsRenderer }: ResponderReportsProps): ReactElement | null => {
    const i = useLocalMessage();

    const columns = columnsRenderer([
        {
            className: 'responder-reports__status',
            render: ({ status }) => (
                <THTag
                    full
                    value={status}
                    color={
                        status === 'Success'
                            ? HTMLColors.GREEN
                            : status === 'Failure'
                            ? HTMLColors.RED
                            : HTMLColors.GRAY
                    }
                />
            ),
        },
        {
            title: [{ label: i('common.name'), sortField: 'responderName' }],
            className: 'responder-reports__name',
            render: ({ responderName }) => <>{responderName}</>,
        },
        {
            title: [{ label: i('responder.list.started-at'), sortField: 'startDate' }],
            className: 'responder-reports__start',
            render: ({ startDate }) => <>{!!startDate && <FormatDate date={startDate} defaultStyle={false} />}</>,
        },
        {
            title: <ColumnTitle>{i('responder.list.ended-at')}</ColumnTitle>,
            className: 'responder-reports__end',
            render: ({ endDate }) => <>{!!endDate && <FormatDate date={endDate} defaultStyle={false} />}</>,
        },
        {
            title: <ColumnTitle>{i('responder.list.cortex-server')}</ColumnTitle>,
            className: 'responder-reports__cortex-server',
            render: ({ cortexId }) => <>{cortexId}</>,
        },
    ]);

    const expandableConfig: ExpandableConfig<ActionOutput> = {
        expandedRowRender: ({ report }) => {
            const reportParsed = typeof report === 'string' ? (JSON.parse(report) as ReportOutput) : report;

            return (
                <div className="responder-reports__expanded__content">
                    {!!reportParsed?.errorMessage && reportParsed?.errorMessage}
                    {!!reportParsed?.full && JSON.stringify(reportParsed?.full, null, 2)}
                </div>
            );
        },
        expandedRowClassName: ({ report }) => {
            const reportParsed = typeof report === 'string' ? (JSON.parse(report) as ReportOutput) : report;

            return classList('responder-reports__expanded', {
                error: reportParsed?.errorMessage,
                full: !!reportParsed?.full,
            });
        },
        rowExpandable: ({ status }) => status !== 'Waiting',
        defaultExpandAllRows: false,
    };

    return (
        <THList>
            <THList.Content
                className="responder-reports__table"
                dataSource={reports}
                columns={columns}
                rowKey="_id"
                expandable={expandableConfig}
            />
        </THList>
    );
};

export default ResponderReports;
